import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Box, Button, Dialog, HStack, Portal, Spinner, Table, Text, VStack } from "@chakra-ui/react";
import { HiOutlineUsers } from "react-icons/hi2";
import { useAuth } from "@/auth/AuthContext";
import AccountUsersService from "@/services/AccountUsersService";
import { toaster } from "@/components/ui/toaster";
import { Field, FormInput } from "@/components/jobOffer/ui/inputs";
import SectionCard from "./SectionCard";

const emptyInvite = { firstName: "", lastName: "", email: "" };

const UsersSection = () => {
    const { t } = useTranslation("account");
    const { isOwner } = useAuth();

    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInviteOpen, setIsInviteOpen] = useState(false);
    const [inviteForm, setInviteForm] = useState(emptyInvite);
    const [isInviting, setIsInviting] = useState(false);
    const [deactivatingId, setDeactivatingId] = useState(null);

    const loadUsers = () => {
        setIsLoading(true);
        AccountUsersService.getUsers()
            .then((data) => setUsers(Array.isArray(data) ? data : []))
            .catch(() => toaster.create({ type: "error", title: t("settingsPage.users.toastLoadError") }))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const handleInvite = async () => {
        if (!inviteForm.firstName.trim() || !inviteForm.lastName.trim() || !inviteForm.email.trim()) return;
        setIsInviting(true);
        try {
            await AccountUsersService.inviteUser(inviteForm);
            toaster.create({ type: "success", title: t("settingsPage.users.toastInviteSuccess") });
            setInviteForm(emptyInvite);
            setIsInviteOpen(false);
            loadUsers();
        } catch {
            toaster.create({ type: "error", title: t("settingsPage.users.toastInviteError") });
        } finally {
            setIsInviting(false);
        }
    };

    const handleDeactivate = async (id) => {
        setDeactivatingId(id);
        try {
            await AccountUsersService.deactivateUser(id);
            toaster.create({ type: "success", title: t("settingsPage.users.toastDeactivateSuccess") });
            loadUsers();
        } catch {
            toaster.create({ type: "error", title: t("settingsPage.users.toastDeactivateError") });
        } finally {
            setDeactivatingId(null);
        }
    };

    return (
        <SectionCard icon={<HiOutlineUsers />} title={t("settingsPage.users.title")} subtitle={t("settingsPage.users.subtitle")}>
            {isOwner && (
                <HStack w="full" justify="flex-end" mb={4}>
                    <Button variant="primary" size="sm" onClick={() => setIsInviteOpen(true)} type="button">
                        {t("settingsPage.users.addButton")}
                    </Button>
                </HStack>
            )}

            {isLoading ? (
                <HStack w="full" py={8} justify="center">
                    <Spinner color="brand.400" size="md" />
                </HStack>
            ) : (
                <Box w="full" overflowX="auto">
                    <Table.Root>
                        <Table.Header>
                            <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                <Table.ColumnHeader py={3} px={4} border="none" color="text.grey" fontSize="xs" textTransform="uppercase">
                                    {t("settingsPage.users.columnFirstName")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader py={3} px={4} border="none" color="text.grey" fontSize="xs" textTransform="uppercase">
                                    {t("settingsPage.users.columnLastName")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader py={3} px={4} border="none" color="text.grey" fontSize="xs" textTransform="uppercase">
                                    {t("settingsPage.users.columnEmail")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader py={3} px={4} border="none" color="text.grey" fontSize="xs" textTransform="uppercase">
                                    {t("settingsPage.users.columnRole")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader py={3} px={4} border="none" color="text.grey" fontSize="xs" textTransform="uppercase">
                                    {t("settingsPage.users.columnStatus")}
                                </Table.ColumnHeader>
                                {isOwner && <Table.ColumnHeader py={3} px={4} border="none" w="1px" />}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {users.length === 0 ? (
                                <Table.Row bg="bg.secondary">
                                    <Table.Cell colSpan={isOwner ? 6 : 5} py={8} border="none" textAlign="center" color="text.grey">
                                        {t("settingsPage.users.empty")}
                                    </Table.Cell>
                                </Table.Row>
                            ) : (
                                users.map((u) => (
                                    <Table.Row key={u.id} bg="bg.secondary" borderBottom="1px solid" borderColor="border.primary">
                                        <Table.Cell py={3} px={4} border="none" color="white">{u.firstName}</Table.Cell>
                                        <Table.Cell py={3} px={4} border="none" color="white">{u.lastName}</Table.Cell>
                                        <Table.Cell py={3} px={4} border="none" color="text.grey">{u.email}</Table.Cell>
                                        <Table.Cell py={3} px={4} border="none">
                                            <Badge colorPalette={u.role === "Owner" ? "purple" : "gray"} size="sm">
                                                {u.role === "Owner" ? t("settingsPage.users.roleOwner") : t("settingsPage.users.roleUser")}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell py={3} px={4} border="none">
                                            <Badge colorPalette={u.isActive ? "green" : "gray"} size="sm">
                                                {u.isActive ? t("settingsPage.users.statusActive") : t("settingsPage.users.statusInactive")}
                                            </Badge>
                                        </Table.Cell>
                                        {isOwner && (
                                            <Table.Cell py={3} px={4} border="none" textAlign="right">
                                                {u.role === "User" && u.isActive && (
                                                    <Button
                                                        variant="outlined"
                                                        size="xs"
                                                        loading={deactivatingId === u.id}
                                                        onClick={() => handleDeactivate(u.id)}
                                                        type="button"
                                                    >
                                                        {t("settingsPage.users.deactivateButton")}
                                                    </Button>
                                                )}
                                            </Table.Cell>
                                        )}
                                    </Table.Row>
                                ))
                            )}
                        </Table.Body>
                    </Table.Root>
                </Box>
            )}

            <Dialog.Root open={isInviteOpen} onOpenChange={(e) => setIsInviteOpen(e.open)} placement="center">
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content bg="bg.card" borderColor="border.primary" border="1px solid">
                            <Dialog.Header>
                                <Dialog.Title color="white">{t("settingsPage.users.inviteModalTitle")}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <VStack gap={4} align="stretch">
                                    <Field label={t("settingsPage.personal.firstName")} required>
                                        <FormInput
                                            value={inviteForm.firstName}
                                            onChange={(e) => setInviteForm((p) => ({ ...p, firstName: e.target.value }))}
                                        />
                                    </Field>
                                    <Field label={t("settingsPage.personal.lastName")} required>
                                        <FormInput
                                            value={inviteForm.lastName}
                                            onChange={(e) => setInviteForm((p) => ({ ...p, lastName: e.target.value }))}
                                        />
                                    </Field>
                                    <Field label={t("settingsPage.users.columnEmail")} required>
                                        <FormInput
                                            type="email"
                                            value={inviteForm.email}
                                            onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                                        />
                                    </Field>
                                </VStack>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button variant="outlined" size="sm" onClick={() => setIsInviteOpen(false)} type="button">
                                    {t("settingsPage.cancelButton")}
                                </Button>
                                <Button variant="primary" size="sm" loading={isInviting} onClick={handleInvite} type="button">
                                    {t("settingsPage.users.inviteModalSubmit")}
                                </Button>
                            </Dialog.Footer>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
        </SectionCard>
    );
};

export default UsersSection;
